import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { Study } from "@/services/studies";

interface StudySelectProps {
  value: string;
  studies: Study[];
  onChange: (studyId: string) => void;
  onNewStudy: () => void;
  placeholder?: string;
}

export default function StudySelect({
  value,
  studies,
  onChange,
  onNewStudy,
  placeholder = "Select a Study",
}: StudySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  const selectedStudy = useMemo(
    () => studies.find((study) => study.id === value),
    [studies, value],
  );
  const filteredStudies = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return studies;
    return studies.filter(
      (study) =>
        study.name.toLowerCase().includes(keyword) || study.code.toLowerCase().includes(keyword),
    );
  }, [query, studies]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        role="combobox"
        aria-label="Study"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        className={`flex w-full cursor-pointer items-center justify-between rounded-lg border bg-white px-3.5 py-2.5 text-sm transition-colors ${
          open ? "border-[#c2388b]" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <span className={selectedStudy ? "truncate text-gray-800" : "truncate text-gray-400"}>
          {selectedStudy ? `${selectedStudy.name} (${selectedStudy.code})` : placeholder}
        </span>
        <i
          className={`ri-arrow-down-s-line shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white"
          style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}
        >
          <div className="border-b border-gray-100 p-2.5">
            <div className="flex items-center gap-2 rounded-md bg-gray-50 px-2.5 py-2">
              <i className="ri-search-line text-sm text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setOpen(false);
                    setQuery("");
                  }
                }}
                aria-label="Search studies"
                placeholder="Search study name or code..."
                className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
                autoFocus
              />
            </div>
          </div>

          <div
            id={listboxId}
            role="listbox"
            aria-label="Studies"
            className="max-h-56 overflow-y-auto"
          >
            {filteredStudies.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-gray-400">No studies found</div>
            ) : (
              filteredStudies.map((study) => (
                <button
                  key={study.id}
                  type="button"
                  role="option"
                  aria-selected={study.id === value}
                  onClick={() => {
                    onChange(study.id);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`w-full cursor-pointer px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-pink-50 ${
                    study.id === value ? "bg-pink-50 text-[#830051]" : "text-gray-700"
                  }`}
                >
                  <div className="font-medium">{study.name}</div>
                  <div className="mt-0.5 font-mono text-xs text-gray-400">{study.code}</div>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-gray-100 p-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setQuery("");
                onNewStudy();
              }}
              className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md bg-pink-50/70 py-2 text-sm font-medium text-[#830051] transition-colors hover:bg-pink-50"
            >
              <i className="ri-add-line" />
              New Study
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
